import React, { useEffect, useState, memo, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Star, ShieldCheck, Users, Check, Hammer, Calculator, TrendingUp, Wrench, Smartphone, X, Flame, Dumbbell, BookOpen, HardHat, Layers, Zap } from 'lucide-react';
// --- Types ---
interface Notification {
  name: string;
  location: string;
  time: string;
}
// --- Constants ---
const NOTIFICATIONS: Notification[] = [{
  name: 'Jorge L.',
  location: 'Buenos Aires',
  time: 'hace 2 min'
}, {
  name: 'Ricardo M.',
  location: 'Córdoba',
  time: 'hace 5 min'
}, {
  name: 'Carlos R.',
  location: 'Rosario',
  time: 'hace 8 min'
}, {
  name: 'Daniel F.',
  location: 'Mendoza',
  time: 'hace 12 min'
}, {
  name: 'Miguel A.',
  location: 'Salta',
  time: 'hace 15 min'
}];
const MODULES = [{
  title: 'MÓDULO 1: LA BIBLIA DEL ACERO Y LA MADERA',
  desc: 'Ingeniería de materiales. Técnicas de soldadura invisible y acabados de lujo para cobrar precios premium.',
  icon: Flame
}, {
  title: 'MÓDULO 2: INGENIERÍA DEL DESPERDICIO CERO',
  desc: 'Cómo optimizar cada barra de hierro de 6 metros. El arte de aprovechar los retazos para convertirlos en stock de venta rápida.',
  icon: TrendingUp
}, {
  title: 'MÓDULO 3: PSICOLOGÍA DEL CATÁLOGO',
  desc: 'Cómo subir el ticket promedio. El arte de ofrecer el combo "Mesa + Bancos" para duplicar tu ganancia en un solo cliente.',
  icon: Users
}, {
  title: 'MÓDULO 4: LA FÁBRICA DE GYM',
  desc: 'Planos de alto rendimiento para Bancos, Racks y Poleas. Entrá al nicho que mejor paga por el peso del hierro.',
  icon: Dumbbell
}];
const BONUSES = [{
  title: 'El Vendedor de Acero (RRSS)',
  desc: 'Venta digital por Marketplace e Instagram las 24hs.',
  icon: Smartphone
}, {
  title: 'Calculadora Anti-Inflación',
  desc: 'Costo exacto de fabricación según el kilo de hierro.',
  icon: Calculator
}, {
  title: 'Ebanistería para Herreros',
  desc: 'Tratá la madera sin ser carpintero. 70% de la ganancia.',
  icon: Layers
}, {
  title: 'Protocolos de Taller Seguro',
  desc: 'Más rápido, más prolijo y sin accidentes.',
  icon: HardHat
}, {
  title: 'Pack de 700 Planos Adicionales',
  desc: 'Si existe, podés fabricarlo. La biblioteca técnica más grande.',
  icon: BookOpen
}];
// --- Components ---
const CountdownTimer = memo(() => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const format = (n: number) => String(n).padStart(2, '0');
  return <div className="flex gap-2 font-mono text-xl md:text-3xl font-bold" data-id="element-2">
      <div className="bg-[#E85D04] text-white px-3 py-2 rounded shadow-inner border border-white/10" data-id="element-3">
        {format(timeLeft.hours)}h
      </div>
      <span className="text-[#E85D04] self-center" data-id="element-4">:</span>
      <div className="bg-[#E85D04] text-white px-3 py-2 rounded shadow-inner border border-white/10" data-id="element-5">
        {format(timeLeft.minutes)}m
      </div>
      <span className="text-[#E85D04] self-center" data-id="element-6">:</span>
      <div className="bg-[#E85D04] text-white px-3 py-2 rounded shadow-inner border border-white/10" data-id="element-7">
        {format(timeLeft.seconds)}s
      </div>
    </div>;
});
const FloatingNotification = () => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setHasScrolled(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (!hasScrolled) return;
    const show = () => {
      setVisible(true);
      setTimeout(() => setVisible(false), 5000);
    };
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % NOTIFICATIONS.length);
      show();
    }, 15000);
    const initial = setTimeout(show, 2000);
    return () => {
      clearInterval(interval);
      clearTimeout(initial);
    };
  }, [hasScrolled]);
  return <AnimatePresence data-id="element-8">
      {visible && <motion.div initial={{
      opacity: 0,
      x: -50,
      y: 20
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      x: -50
    }} className="fixed bottom-6 left-6 z-50 bg-white shadow-2xl rounded-xl p-4 border border-slate-100 flex items-center gap-4 max-w-sm" data-id="element-9">
          <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center text-[#E85D04]" data-id="element-10">
            <Check size={24} data-id="element-11" />
          </div>
          <div data-id="element-12">
            <p className="text-[10px] font-black text-[#E85D04] uppercase tracking-widest mb-0.5" data-id="element-13">
              Compra Verificada
            </p>
            <p className="text-sm text-slate-800 leading-tight" data-id="element-14">
              <span className="font-bold" data-id="element-15">{NOTIFICATIONS[index].name}</span> de{' '}
              {NOTIFICATIONS[index].location} <br data-id="element-16" />
              compró el <span className="font-bold" data-id="element-17">sistema completo</span> 🔩
            </p>
            <p className="text-[10px] text-slate-400 mt-1" data-id="element-18">
              {NOTIFICATIONS[index].time}
            </p>
          </div>
          <button onClick={() => setVisible(false)} className="text-slate-300 hover:text-slate-500 ml-2" data-id="element-19">
            <X size={16} data-id="element-20" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
const CTAButton = ({
  children,
  href = '#pricing',
  className = ''
}: {
  children: React.ReactNode;
  href?: string;
  className?: string;
}) => <motion.a href={href} animate={{
  scale: [1, 1.02, 1]
}} transition={{
  duration: 2,
  repeat: Infinity,
  ease: 'easeInOut'
}} whileHover={{
  scale: 1.05
}} whileTap={{
  scale: 0.98
}} className={`
      inline-flex items-center justify-center gap-2 bg-[#E85D04] text-white px-8 py-5 rounded-lg
      font-black text-lg md:text-xl shadow-[0_10px_25px_-5px_rgba(232,93,4,0.5)]
      hover:bg-[#BC3908] transition-colors uppercase tracking-tight text-center
      ${className}
    `} data-id="element-21">
    {children}
  </motion.a>;
export function LandingPage() {
  useEffect(() => {
    // Meta Pixel Code
    const fbq = (window as any).fbq || function () {
      ;
      (fbq as any).q = (fbq as any).q || [];
      (fbq as any).q.push(arguments);
    };
    (window as any).fbq = fbq;
    if (!(window as any)._fbq) (window as any)._fbq = fbq;
    (fbq as any).push = fbq;
    (fbq as any).loaded = true;
    (fbq as any).version = '2.0';
    (fbq as any).queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
    fbq('init', '929826529816985');
    fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-[#F5EFE6] selection:bg-[#E85D04] selection:text-white" data-id="element-22">
      <FloatingNotification data-id="element-23" />

      {/* Urgency Banner */}

      {/* Hero Section */}
      <header className="relative bg-[#0d0d0d] text-white pt-8 pb-12 md:pt-24 md:pb-32 overflow-hidden metal-texture min-h-[90vh] md:min-h-0 flex flex-col justify-center" data-id="element-24">
        <div className="absolute inset-0 opacity-30 pointer-events-none" data-id="element-25">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_rgba(232,93,4,0.18)_0%,_transparent_70%)]" data-id="element-26" />
        </div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-27">
          <div className="max-w-5xl mx-auto text-center" data-id="element-28">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block bg-[#E85D04]/15 border border-[#E85D04]/40 px-3 py-1 rounded text-[#F5EFE6] text-[10px] md:text-sm font-bold mb-4 md:mb-8 backdrop-blur-sm uppercase tracking-widest" data-id="element-29">
              🔩 SISTEMA HERRERÍA RENTABLE 2026™
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="md:text-8xl font-black mb-3 md:mb-6 uppercase text-[28px] leading-tight" data-id="element-30">
              La Biblia del <br data-id="element-31" />
              <span className="text-[#E85D04]" data-id="element-32">Herrero Emprendedor.</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="md:text-2xl text-slate-300 mb-6 md:mb-12 max-w-3xl mx-auto font-light text-[14px] md:text-[16px] leading-relaxed px-4" data-id="element-33">
              El método probado para fabricar muebles industriales, máquinas de
              gym y blindar tu taller contra la inflación vendiendo por
              internet.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3
          }} className="relative max-w-[280px] md:max-w-3xl mx-auto mb-8 md:mb-16" data-id="element-34">
              <div className="relative z-10 bg-zinc-900 p-1.5 md:p-4 rounded-xl md:rounded-2xl shadow-2xl border border-zinc-800" data-id="element-35">
                <img src="https://cdn.magicpatterns.com/uploads/bfFnUHr5BLwBBR2jKbrDvG/mockup_sistema_herreria_rentable_web.webp" alt="Sistema Herrería Rentable 2026 - Maestro herrero" className="w-full h-auto rounded-lg md:rounded-xl shadow-lg" data-id="element-36" />
                <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 bg-[#E85D04] text-white px-3 py-1.5 md:px-6 md:py-3 rounded-md md:rounded-lg shadow-2xl font-black text-[10px] md:text-lg border-2 border-[#0d0d0d] uppercase" data-id="element-37">
                  Edición 2026
                </div>
              </div>
              <div className="absolute -inset-4 bg-[#E85D04]/20 blur-3xl rounded-full opacity-50" data-id="element-38" />
            </motion.div>

            <div className="flex flex-col items-center gap-4 md:gap-8" data-id="element-39">
              <CTAButton className="w-full md:w-auto px-8 py-4 md:px-12 md:py-6 text-base md:text-xl" data-id="element-40">
                → QUIERO LLEVAR MI TALLER AL SIGUIENTE NIVEL
              </CTAButton>

              <div className="flex flex-wrap justify-center gap-3 md:gap-8 text-slate-400 text-[10px] md:text-sm font-medium" data-id="element-41">
                <div className="flex items-center gap-1.5" data-id="element-42">
                  <CheckCircle2 size={14} className="text-[#E85D04]" data-id="element-43" /> +700
                  Planos
                </div>
                <div className="flex items-center gap-1.5" data-id="element-44">
                  <CheckCircle2 size={14} className="text-[#E85D04]" data-id="element-45" /> Acceso
                  Instantáneo
                </div>
                <div className="flex items-center gap-1.5" data-id="element-46">
                  <CheckCircle2 size={14} className="text-[#E85D04]" data-id="element-47" /> Garantía
                  7 Días
                </div>
              </div>

              <div className="bg-zinc-900/80 border border-white/10 rounded-xl p-4 md:p-6 mt-2 md:mt-4 backdrop-blur-sm scale-90 md:scale-100" data-id="element-48">
                <p className="text-white/60 font-black uppercase tracking-[0.2em] text-[9px] md:text-xs mb-2 md:mb-3 text-center" data-id="element-49">
                  ⏰ EL PRECIO PROMOCIONAL CIERRA EN:
                </p>
                <CountdownTimer data-id="element-50" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="bg-[#E85D04] py-8 md:py-12 text-white" data-id="element-51">
        <div className="container mx-auto px-4" data-id="element-52">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto text-center" data-id="element-53">
            {[{
            label: 'Herreros',
            val: '+2.500'
          }, {
            label: 'Planos',
            val: '700+'
          }, {
            label: 'Módulos',
            val: '60'
          }, {
            label: 'Calificación',
            val: '4.9/5',
            stars: true
          }].map((s, i) => <div key={i} data-id="element-54">
                <p className="text-3xl md:text-5xl font-black mb-1 font-display" data-id="element-55">
                  {s.val}
                </p>
                {s.stars && <div className="flex justify-center text-yellow-300 mb-2" data-id="element-56">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-57" />)}
                  </div>}
                <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-black opacity-90" data-id="element-58">
                  {s.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-20 bg-white" data-id="element-59">
        <div className="container mx-auto px-4" data-id="element-60">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-61">
            {[{
            q: 'Con la calculadora del Bono 2 supe exactamente cuánto cobrar por una mesa industrial y no perder con el aumento del hierro.',
            n: 'Jorge L.',
            l: 'Buenos Aires',
            i: 'JL'
          }, {
            q: 'Fabriqué mi primer rack de gym con los planos y lo vendí en 48hs por Marketplace.',
            n: 'Ricardo M.',
            l: 'Maestro Herrero',
            i: 'RM'
          }, {
            q: 'Por fin un material que entiende la realidad de nuestro taller y nos enseña a vender.',
            n: 'Carlos R.',
            l: 'Rosario',
            i: 'CR'
          }].map((t, i) => <div key={i} className="bg-[#F5EFE6] p-8 rounded-2xl shadow-sm border border-[#E85D04]/10" data-id="element-62">
                <div className="flex gap-1 mb-4 text-[#E85D04]" data-id="element-63">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-64" />)}
                </div>
                <p className="text-slate-800 italic mb-6 leading-relaxed" data-id="element-65">
                  "{t.q}"
                </p>
                <div className="flex items-center gap-4" data-id="element-66">
                  <div className="w-10 h-10 bg-[#E85D04] rounded-full flex items-center justify-center font-bold text-white" data-id="element-67">
                    {t.i}
                  </div>
                  <div data-id="element-68">
                    <p className="font-bold text-slate-900" data-id="element-69">{t.n}</p>
                    <p className="text-xs text-slate-500" data-id="element-70">{t.l}</p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-[#0d0d0d] text-white metal-texture" data-id="element-71">
        <div className="container mx-auto px-4" data-id="element-72">
          <div className="max-w-4xl mx-auto" data-id="element-73">
            <div className="text-center mb-10 md:mb-16" data-id="element-74">
              <h2 className="text-3xl md:text-6xl font-black mb-4 md:mb-6 leading-tight uppercase" data-id="element-75">
                ¿Cuántas horas vas a pasar soldando sintiendo que el dinero se
                te escapa?
              </h2>
              <p className="text-lg md:text-xl text-slate-300" data-id="element-76">
                Suben los perfiles de hierro. Sube el electrodo. Los clientes
                regatean. No es falta de oficio, es falta de un{' '}
                <span className="text-[#E85D04] font-bold" data-id="element-77">
                  sistema de fabricación y venta real
                </span>
                .
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4" data-id="element-78">
              {['Cotizando "a ojo" y perdiendo plata en cada presupuesto.', 'Con desperdicios de material que se comen tu ganancia neta.', 'Perdiendo ventas porque no sabés cómo publicar en redes sociales.', 'Haciendo "arreglitos" mientras otros venden muebles premium.'].map((p, i) => <div key={i} className="bg-white/5 border border-white/10 p-6 rounded-xl flex gap-4 items-start backdrop-blur-sm" data-id="element-79">
                  <XCircle className="text-[#E85D04] shrink-0 mt-1" size={24} data-id="element-80" />
                  <p className="text-white/90 font-medium text-base md:text-lg" data-id="element-81">
                    {p}
                  </p>
                </div>)}
            </div>

            <div className="mt-10 md:mt-16 bg-[#E85D04]/10 border-l-8 border-[#E85D04] p-8 md:p-12 rounded-r-3xl" data-id="element-82">
              <p className="text-2xl md:text-4xl font-black text-white leading-tight italic uppercase" data-id="element-83">
                "¿Y si esta fuera la última vez que enfrentás un aumento de
                materiales sin saber cómo ajustar tus precios?"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Hero */}
      <section className="py-12 md:py-20 bg-white text-center" data-id="element-84">
        <div className="container mx-auto px-4" data-id="element-85">
          <div className="inline-block bg-[#E85D04] text-white px-5 py-1.5 rounded font-black text-xs uppercase tracking-widest mb-4 md:mb-6" data-id="element-86">
            SOLUCIÓN INTEGRAL
          </div>
          <h2 className="text-3xl md:text-6xl font-black mb-4 md:mb-6 uppercase" data-id="element-87">
            🔩 Sistema Herrería Rentable 2026™
          </h2>
          <p className="text-lg md:text-2xl text-slate-600 max-w-3xl mx-auto" data-id="element-88">
            <span className="font-bold text-slate-900" data-id="element-89">
              Ebook Central + 5 Bonos Estratégicos.
            </span>{' '}
            100% adaptado a la realidad de Argentina y LATAM. Dejá de improvisar
            y empezá a fabricar en serie.
          </p>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-16 md:py-24 bg-[#F5EFE6]" data-id="element-90">
        <div className="container mx-auto px-4" data-id="element-91">
          <div className="text-center mb-10 md:mb-16" data-id="element-92">
            <span className="text-[#E85D04] font-black uppercase tracking-widest text-sm" data-id="element-93">
              📚 Contenido Premium
            </span>
            <h2 className="text-4xl md:text-6xl font-black mt-3 md:mt-4 mb-4 md:mb-6 uppercase" data-id="element-94">
              La Estructura Maestra para Transformar tu Taller
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-95">
            {MODULES.map((m, i) => <motion.div key={i} whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 border-t-8 border-[#E85D04]" data-id="element-96">
                <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center text-[#E85D04] mb-6" data-id="element-97">
                  <m.icon size={32} data-id="element-98" />
                </div>
                <h3 className="text-2xl font-black mb-4 text-slate-900 uppercase leading-tight" data-id="element-99">
                  {m.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-lg font-sans" data-id="element-100">
                  {m.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-16 md:py-24 bg-white overflow-hidden" data-id="element-101">
        <div className="container mx-auto px-4" data-id="element-102">
          <div className="text-center mb-10 md:mb-16" data-id="element-103">
            <div className="inline-block bg-[#E85D04] text-white px-6 py-2 rounded font-black text-sm uppercase tracking-widest mb-4 md:mb-6" data-id="element-104">
              🎁 VALOR TOTAL: $55.000 → GRATIS
            </div>
            <h2 className="text-4xl md:text-6xl font-black uppercase" data-id="element-105">
              5 Bonos Estratégicos
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-106">
            {BONUSES.map((b, i) => <div key={i} className="bg-[#F5EFE6] p-6 rounded-2xl border border-[#E85D04]/10 relative group hover:border-[#E85D04] transition-colors" data-id="element-107">
                <div className="absolute -top-3 -right-3 bg-[#E85D04] text-white text-[10px] font-black px-3 py-1 rounded uppercase" data-id="element-108">
                  Gratis
                </div>
                <div className="text-[#E85D04] mb-4 group-hover:scale-110 transition-transform" data-id="element-109">
                  <b.icon size={28} data-id="element-110" />
                </div>
                <h4 className="text-lg font-black mb-2 leading-tight uppercase text-slate-900" data-id="element-111">
                  BONO {i + 1}: {b.title}
                </h4>
                <p className="text-slate-600 text-sm font-sans" data-id="element-112">{b.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 md:py-24 bg-[#0d0d0d] text-white relative metal-texture" data-id="element-113">
        <div className="container mx-auto px-4 relative z-10" data-id="element-114">
          <div className="max-w-4xl mx-auto" data-id="element-115">
            <div className="text-center mb-8 md:mb-12" data-id="element-116">
              <h2 className="md:text-7xl font-black mb-3 md:mb-6 uppercase text-[20px]" data-id="element-117">
                Llevate tu Pack Completo Hoy ⭐
              </h2>
              <p className="text-slate-400 text-sm md:text-lg" data-id="element-118">
                Acceso inmediato a todo el material en formato PDF.
              </p>
            </div>

            <div className="bg-zinc-900 rounded-2xl md:rounded-3xl py-4 px-5 md:p-16 border-2 md:border-4 border-[#E85D04] shadow-[0_0_60px_rgba(232,93,4,0.3)] relative overflow-hidden" data-id="element-119">
              <div className="absolute top-0 left-0 w-full h-1 md:h-2 bg-gradient-to-r from-transparent via-[#E85D04] to-transparent" data-id="element-120" />

              <div className="grid md:grid-cols-2 gap-4 md:gap-12 items-center" data-id="element-121">
                <div data-id="element-122">
                  <h3 className="text-xl md:text-3xl font-black mb-1 md:mb-2 uppercase" data-id="element-123">
                    SISTEMA HERRERÍA RENTABLE 2026™
                  </h3>
                  <p className="text-white/50 text-[10px] md:text-sm mb-3 md:mb-8" data-id="element-124">
                    Ebook Central + 5 Bonos Estratégicos
                  </p>
                  <ul className="grid grid-cols-1 gap-1.5 md:gap-4 mb-4 md:mb-8" data-id="element-125">
                    {['Módulos de Acero y Madera', 'Calculadora Anti-Inflación', '700+ Planos Maestros', 'Bono RRSS y Marketplace', 'Protocolos de Taller Seguro', 'Acceso inmediato de por vida'].map((f, i) => <li key={i} className="flex items-center gap-2 md:gap-3 text-slate-300 font-medium text-[11px] md:text-base" data-id="element-126">
                        <CheckCircle2 size={12} className="text-[#E85D04] shrink-0" data-id="element-127" />{' '}
                        {f}
                      </li>)}
                  </ul>
                </div>

                <div className="text-center bg-zinc-800/50 p-4 md:p-8 rounded-xl md:rounded-2xl border border-white/5" data-id="element-128">
                  <p className="text-zinc-500 line-through text-sm md:text-xl mb-0.5 md:mb-2" data-id="element-129">
                    $65.000 ARS
                  </p>
                  <div className="flex flex-col items-center mb-3 md:mb-6" data-id="element-130">
                    <span className="text-4xl md:text-8xl font-black text-[#E85D04] font-display leading-none" data-id="element-131">
                      $19.990
                    </span>
                    <span className="text-[10px] md:text-xl font-bold tracking-widest text-zinc-400" data-id="element-132">
                      ARS
                    </span>
                  </div>

                  <div className="mb-4 md:mb-8 flex flex-col items-center scale-75 md:scale-100 origin-center" data-id="element-133">
                    <p className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 mb-1 md:mb-4" data-id="element-134">
                      ⏰ LA OFERTA TERMINA EN:
                    </p>
                    <CountdownTimer data-id="element-135" />
                  </div>

                  <CTAButton href="https://checkout.guiadigital360.online/cart/43889650401349:1" className="w-full py-3 md:py-4 text-sm md:text-base" data-id="element-136">
                    → QUIERO EL SISTEMA AHORA
                  </CTAButton>

                  <div className="mt-4 md:mt-8 pt-4 md:pt-6 border-t border-white/5" data-id="element-137">
                    <p className="text-zinc-400 text-[10px] md:text-xs mb-3 leading-relaxed" data-id="element-138">
                      ¿Tuviste problemas para pagar o no tenés mercado pago?
                    </p>
                    <motion.a href="https://wa.me/5492966244076?text=Hola,%20vengo%20desde%20la%20p%C3%A1gina%20de%20SISTEMA%20CARNICER%C3%8DAS%20RENTABLES.%20Quiero%20comprar%20el%20material,%20pero%20tuve%20problemas%20para%20pagar%20o%20prefiero%20recibir%20ayuda%20por%20WhatsApp.%20%C2%BFMe%20ayud%C3%A1s%20a%20finalizar%20la%20compra?" target="_blank" rel="noopener noreferrer" animate={{
                    scale: [1, 1.03, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.98
                  }} className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-lg font-bold text-xs md:text-sm uppercase tracking-tight w-full shadow-lg shadow-green-500/20" data-id="element-139">
                      ESCRIBINOS POR WHATSAPP Y TE AYUDAMOS
                    </motion.a>
                  </div>

                  <p className="mt-4 text-[#E85D04] font-black text-[10px] md:text-sm uppercase tracking-tight" data-id="element-140">
                    ⚠️ Después del contador, el precio sube $10.000.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-141">
        <div className="container mx-auto px-4" data-id="element-142">
          <div className="text-center mb-10 md:mb-16" data-id="element-143">
            <h2 className="text-4xl md:text-6xl font-black uppercase" data-id="element-144">
              ⚖️ ¿Cuánto cuesta no tenerlo?
            </h2>
            <p className="text-slate-600 mt-2 md:mt-4 text-lg" data-id="element-145">
              La diferencia entre un taller que sobrevive y uno que prospera.
            </p>
          </div>

          {/* Mobile */}
          <div className="block md:hidden space-y-4 max-w-sm mx-auto" data-id="element-146">
            {[{
            s: 'Sube el corralón',
            n: 'Perdés margen o te quedas sin stock.',
            y: 'Blindás tu ganancia con la Calculadora Pro.'
          }, {
            s: 'Marketplace te ignora',
            n: 'Publicaciones que nadie mira.',
            y: 'Bono 1: Fotos y títulos que cortan el scroll.'
          }, {
            s: 'Sobran retazos',
            n: 'Se oxidan en un rincón.',
            y: 'Módulo 2: Fabricás productos de caja rápida.'
          }].map((row, i) => <div key={i} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-147">
                <div className="bg-[#0d0d0d] px-4 py-3 text-xs font-black text-white uppercase tracking-wider" data-id="element-148">
                  {row.s}
                </div>
                <div className="grid grid-cols-2 text-[11px] leading-tight" data-id="element-149">
                  <div className="bg-red-50 p-4 text-red-700 italic border-r border-white" data-id="element-150">
                    <span className="block font-black text-[9px] uppercase mb-1 opacity-60" data-id="element-151">
                      Sin el Sistema
                    </span>
                    {row.n}
                  </div>
                  <div className="bg-green-50 p-4 text-green-800 font-bold" data-id="element-152">
                    <span className="block font-black text-[9px] uppercase mb-1 opacity-60" data-id="element-153">
                      Con el Sistema
                    </span>
                    {row.y}
                  </div>
                </div>
              </div>)}
          </div>

          {/* Desktop */}
          <div className="hidden md:block max-w-5xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xl" data-id="element-154">
            <table className="w-full text-left border-collapse" data-id="element-155">
              <thead data-id="element-156">
                <tr className="bg-[#0d0d0d] text-white" data-id="element-157">
                  <th className="p-6 md:p-8 font-black text-lg uppercase" data-id="element-158">
                    Situación
                  </th>
                  <th className="p-6 md:p-8 font-black text-lg uppercase" data-id="element-159">
                    ❌ Sin el Sistema
                  </th>
                  <th className="p-6 md:p-8 font-black text-lg uppercase" data-id="element-160">
                    ✅ Con el Sistema
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-161">
                {[{
                s: 'Sube el corralón',
                n: 'Perdés margen o te quedas sin stock.',
                y: 'Blindás tu ganancia con la Calculadora Pro.'
              }, {
                s: 'Marketplace te ignora',
                n: 'Publicaciones que nadie mira.',
                y: 'Bono 1: Fotos y títulos que cortan el scroll.'
              }, {
                s: 'Sobran retazos',
                n: 'Se oxidan en un rincón.',
                y: 'Módulo 2: Fabricás productos de caja rápida.'
              }].map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-162">
                    <td className="p-6 md:p-8 font-black text-slate-900" data-id="element-163">
                      {row.s}
                    </td>
                    <td className="p-6 md:p-8 text-red-700 italic font-medium" data-id="element-164">
                      {row.n}
                    </td>
                    <td className="p-6 md:p-8 text-green-700 font-black" data-id="element-165">
                      {row.y}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-16 md:py-24 bg-[#E85D04]/5" data-id="element-166">
        <div className="container mx-auto px-4" data-id="element-167">
          <div className="max-w-4xl mx-auto bg-white p-10 md:p-20 rounded-3xl border-4 border-[#E85D04] shadow-2xl text-center relative overflow-hidden" data-id="element-168">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#E85D04]/5 rounded-bl-full" data-id="element-169" />

            <motion.img src="https://cdn.magicpatterns.com/uploads/umugJyXQfqVuNVgDtauhPU/garantia-7-dias-250.webp" alt="Garantía de 7 días" className="w-32 md:w-40 mx-auto mb-6 md:mb-8" initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} data-id="element-170" />

            <h2 className="text-2xl md:text-5xl font-black mb-6 md:mb-8 uppercase" data-id="element-171">
              🛡️ Garantía de Satisfacción de 7 Días
            </h2>
            <p className="text-lg md:text-2xl text-slate-600 leading-relaxed mb-8 md:mb-12" data-id="element-172">
              Si el material no mejora la rentabilidad de tu taller en la
              primera semana, te devolvemos el 100%.
              <br data-id="element-173" />
              <span className="text-[#E85D04] font-black uppercase mt-3 md:mt-4 block" data-id="element-174">
                Sin preguntas.
              </span>
            </p>
            <CTAButton href="https://checkout.guiadigital360.online/cart/43889650401349:1" data-id="element-175">
              PROBAR SIN RIESGO
            </CTAButton>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0d0d0d] text-white/40 py-12 md:py-20 border-t border-white/5 metal-texture" data-id="element-176">
        <div className="container mx-auto px-4 text-center" data-id="element-177">
          <p className="font-black text-white text-3xl mb-6 tracking-tight uppercase font-display" data-id="element-178">
            🔩 Sistema Herrería Rentable™
          </p>
          <p className="max-w-2xl mx-auto text-sm leading-relaxed mb-12" data-id="element-179">
            El método definitivo para el herrero moderno que busca
            profesionalizar su oficio y proteger su capital.
          </p>
          <div className="flex justify-center gap-8 text-xs font-bold uppercase tracking-widest mb-12" data-id="element-180">
            <a href="#" className="hover:text-white transition-colors" data-id="element-181">
              Términos
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-182">
              Privacidad
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-183">
              Soporte
            </a>
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em]" data-id="element-184">
            © 2026 — Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}